import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Upload, FileAudio, ChevronDown } from 'lucide-react';
import {
  TRANSCRIPTION_LANGUAGES,
  DEFAULT_TRANSCRIPTION_LANGUAGE,
  findLanguage,
} from '../lib/transcriptionLanguages';

/**
 * Upload flow with session metadata. Replaces the bare native file picker.
 *
 * Collects:
 *   - session_type: 'СС' (стратегическая сессия) | 'ПК' (проработка) | 'РР' (разбор)
 *   - client: человек из гипнозио (id + имя); свободный текст остаётся
 *     запасным вариантом для тех, кого в гипнозио нет
 *   - therapy_session_id: встреча из журнала терапии, если такая заведена
 *   - visibility: кому запись будет слышна
 *   - session_date: YYYY-MM-DD (native <input type="date">)
 *   - file: MP3
 *
 * Renders live title preview composed server-side as:
 *   "{session_type} - {client_name} | DD.MM.YYYY"
 *
 * Выбор человека из списка — это не удобство, а основа права: пока у записи
 * нет id клиента, аудитория «клиенту» ничего не открывает, потому что открывать
 * некому. Строка «Марина» правом не является.
 */
const SESSION_TYPES = [
  { code: 'СС', label: 'СС', full: 'Стратегическая сессия' },
  { code: 'ПК', label: 'ПК', full: 'Проработка' },
  { code: 'РР', label: 'РР', full: 'Разбор' },
];

const VISIBILITIES = [
  { code: 'private', label: 'Только мне', hint: 'Слышат загрузивший и админы' },
  { code: 'client', label: 'Клиенту', hint: 'Плюс тот, чья это проработка' },
  { code: 'students', label: 'Студентам', hint: 'Учебный материал для всех с доступом к курсу' },
  { code: 'public', label: 'Всем вошедшим', hint: 'Любой, кто вошёл в гипновейв' },
];

// Что подставляем при смене типа. Разбор — единственное, что похоже на
// учебный материал; проработка и стратегическая всегда остаются личными.
const SUGGESTED_VISIBILITY = { 'СС': 'client', 'ПК': 'client', 'РР': 'students' };

const todayISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateDMY = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const UploadModal = ({ onClose, onSubmit, disabled, searchClients, fetchClientSessions }) => {
  const [sessionType, setSessionType] = useState('СС');
  const [clientName, setClientName] = useState('');
  // Выбранный человек из гипнозио. Пока он не выбран, у записи есть только
  // имя строкой — и аудитория «клиенту» работать не будет.
  const [clientProfile, setClientProfile] = useState(null);
  const [clientMatches, setClientMatches] = useState([]);
  const [clientListOpen, setClientListOpen] = useState(false);
  const [clientsUnavailable, setClientsUnavailable] = useState(false);
  const [therapySessions, setTherapySessions] = useState([]);
  const [therapySessionId, setTherapySessionId] = useState('');
  const [visibility, setVisibility] = useState('client');
  // Пока человек не трогал выбор аудитории сам, она следует за типом сессии.
  const [visibilityTouched, setVisibilityTouched] = useState(false);
  const [sessionDate, setSessionDate] = useState(todayISO());
  const [language, setLanguage] = useState(DEFAULT_TRANSCRIPTION_LANGUAGE);
  const [langOpen, setLangOpen] = useState(false);
  const [mode, setMode] = useState('single'); // 'single' | 'dual'
  const [file, setFile] = useState(null); // single-mode file
  const [expertFile, setExpertFile] = useState(null); // dual-mode expert track
  const [clientFile, setClientFile] = useState(null); // dual-mode client track
  const fileInputRef = useRef(null);
  const expertInputRef = useRef(null);
  const clientInputRef = useRef(null);
  const firstInputRef = useRef(null);
  const langPopRef = useRef(null);
  const langBtnRef = useRef(null);

  useEffect(() => {
    // Autofocus on the client name field — most common entry point.
    firstInputRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') {
      if (langOpen) { setLangOpen(false); return; }
      onClose();
    }};
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, langOpen]);

  // Close the language dropdown when clicking outside it.
  useEffect(() => {
    if (!langOpen) return;
    const onDocClick = (e) => {
      if (langPopRef.current?.contains(e.target)) return;
      if (langBtnRef.current?.contains(e.target)) return;
      setLangOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [langOpen]);

  const selectedLang = findLanguage(language);

  // Аудитория идёт за типом сессии, пока её не выбрали руками.
  useEffect(() => {
    if (visibilityTouched) return;
    setVisibility(SUGGESTED_VISIBILITY[sessionType] || 'client');
  }, [sessionType, visibilityTouched]);

  // Поиск людей в гипнозио по мере набора имени. Задержка, чтобы не дёргать
  // сервер на каждую букву; выбранного человека поиск не сбрасывает.
  //
  // От двух букв, а не с пустого поля: иначе список раскрывался бы сразу при
  // открытии формы (поле в фокусе) и закрывал бы собой всё, что ниже.
  useEffect(() => {
    if (!searchClients || clientProfile) return;
    const query = clientName.trim();
    if (query.length < 2) {
      setClientMatches([]);
      setClientListOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      const found = await searchClients(query);
      setClientsUnavailable(found === null);
      setClientMatches(found || []);
      setClientListOpen(!!found && found.length > 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientName, clientProfile, searchClients]);

  // Встречи журнала выбранного клиента — можно привязать запись к конкретной.
  useEffect(() => {
    if (!fetchClientSessions || !clientProfile) {
      setTherapySessions([]);
      setTherapySessionId('');
      return;
    }
    let cancelled = false;
    fetchClientSessions(clientProfile.id).then((list) => {
      if (!cancelled) setTherapySessions(list);
    });
    return () => { cancelled = true; };
  }, [clientProfile, fetchClientSessions]);

  const chooseClient = (profile) => {
    setClientProfile(profile);
    setClientName(profile.name);
    setClientListOpen(false);
  };

  const clearClient = () => {
    setClientProfile(null);
    setTherapySessionId('');
  };

  const title = useMemo(() => {
    const safeName = clientName.trim() || '(без имени)';
    return `${sessionType} - ${safeName} | ${formatDateDMY(sessionDate)}`;
  }, [sessionType, clientName, sessionDate]);

  const canSubmit = (() => {
    if (disabled) return false;
    if (!clientName.trim() || !sessionDate) return false;
    if (mode === 'single') return !!file;
    return !!expertFile && !!clientFile;
  })();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const metadata = {
      session_type: sessionType,
      client_name: clientName.trim(),
      session_date: sessionDate,
      title,
      language,
      visibility,
      client_profile_id: clientProfile?.id || null,
      client_profile_name: clientProfile?.name || null,
      therapy_session_id: therapySessionId || null,
    };
    if (mode === 'single') {
      onSubmit({ mode: 'single', file }, metadata);
    } else {
      onSubmit({ mode: 'dual', expertFile, clientFile }, metadata);
    }
  };

  const validateAndSet = (f, setter) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.mp3')) {
      alert('Только MP3 файлы');
      return;
    }
    setter(f);
  };
  const handleFileChosen = (f) => validateAndSet(f, setFile);
  const handleExpertChosen = (f) => validateAndSet(f, setExpertFile);
  const handleClientChosen = (f) => validateAndSet(f, setClientFile);

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="upload-modal-backdrop"
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4"
        data-testid="upload-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Upload size={18} /> Загрузка сессии
          </h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white" data-testid="upload-modal-close">
            <X size={18} />
          </button>
        </div>

        {/* Session type toggle */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Тип сессии</label>
          <div className="grid grid-cols-3 gap-1 bg-gray-900 rounded p-0.5" role="radiogroup">
            {SESSION_TYPES.map((s) => (
              <button
                key={s.code}
                type="button"
                role="radio"
                aria-checked={sessionType === s.code}
                title={s.full}
                onClick={() => setSessionType(s.code)}
                className={`py-1.5 text-sm rounded transition-colors ${
                  sessionType === s.code
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
                data-testid={`session-type-${s.code}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            {SESSION_TYPES.find((s) => s.code === sessionType)?.full}
          </p>
        </div>

        {/* Клиент — выбор человека из гипнозио. От него зависит право слушать. */}
        <div className="relative">
          <label className="block text-xs text-gray-400 mb-1.5" htmlFor="client-name-input">
            Клиент
          </label>
          <div className="flex gap-2">
            <input
              id="client-name-input"
              ref={firstInputRef}
              type="text"
              value={clientName}
              onChange={(e) => { setClientName(e.target.value); clearClient(); }}
              onFocus={() => clientMatches.length > 0 && !clientProfile && setClientListOpen(true)}
              placeholder="Начните вводить имя"
              maxLength={120}
              autoComplete="off"
              className={`flex-1 bg-gray-900 border rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 ${
                clientProfile ? 'border-green-700' : 'border-gray-700'
              }`}
              data-testid="upload-modal-client-name"
            />
            {clientProfile && (
              <button
                type="button"
                onClick={() => { clearClient(); setClientName(''); }}
                className="px-2 text-gray-500 hover:text-white"
                title="Отвязать от аккаунта"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {clientListOpen && !clientProfile && clientMatches.length > 0 && (
            <div
              className="absolute z-20 left-0 right-0 top-[calc(100%+4px)] max-h-56 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-1"
              data-testid="upload-modal-client-list"
            >
              {/* Второй строкой — то, чем человека можно отличить от тёзки:
                  ник, почта и число встреч в журнале. Имён вроде «Екатерина»
                  в базе может быть несколько, и по одному имени выбрать
                  правильного нельзя. */}
              {clientMatches.map((c) => {
                const marks = [
                  c.telegram_username && `@${c.telegram_username}`,
                  c.vk_username && `vk/${c.vk_username}`,
                  c.email,
                  c.sessions_count > 0 && `журнал: ${c.sessions_count}`,
                ].filter(Boolean);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => chooseClient(c)}
                    className="w-full text-left px-3 py-1.5 hover:bg-gray-800 transition-colors"
                  >
                    <span className="block text-sm text-gray-200 truncate">{c.name}</span>
                    {marks.length > 0 && (
                      <span className="block text-[11px] text-gray-500 truncate">
                        {marks.join(' · ')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {clientProfile ? (
            <p className="text-[10px] text-green-500 mt-1">
              Привязано к аккаунту Gipnozio — запись откроется этому человеку
            </p>
          ) : (
            <p className="text-[10px] text-gray-500 mt-1">
              {clientsUnavailable
                ? 'Gipnozio не отвечает — имя сохранится текстом, без привязки'
                : 'Начните вводить имя и выберите человека из списка — иначе запись останется закрытой для клиента'}
            </p>
          )}
        </div>

        {/* Встреча из журнала терапии — если она заведена в гипнозио */}
        {therapySessions.length > 0 && (
          <div>
            <label className="block text-xs text-gray-400 mb-1.5" htmlFor="therapy-session-select">
              Встреча в журнале
            </label>
            <select
              id="therapy-session-select"
              value={therapySessionId}
              onChange={(e) => setTherapySessionId(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
              data-testid="upload-modal-therapy-session"
            >
              <option value="">Не привязывать</option>
              {therapySessions.map((s) => (
                <option key={s.id} value={s.id}>
                  №{s.session_number}
                  {s.conducted_at ? ` — ${formatDateDMY(String(s.conducted_at).slice(0, 10))}` : ''}
                  {s.title ? ` · ${s.title}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Кому будет слышна запись */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5" htmlFor="visibility-select">
            Кому слышно
          </label>
          <select
            id="visibility-select"
            value={visibility}
            onChange={(e) => { setVisibility(e.target.value); setVisibilityTouched(true); }}
            className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
            data-testid="upload-modal-visibility"
          >
            {VISIBILITIES.map((v) => (
              <option key={v.code} value={v.code}>{v.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-gray-500 mt-1">
            {VISIBILITIES.find((v) => v.code === visibility)?.hint}
            {visibility === 'client' && !clientProfile && ' — клиент не выбран, запись будет закрыта'}
          </p>
        </div>

        {/* Session date + transcription language picker (pictogram button) */}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-400 mb-1.5" htmlFor="session-date-input">
              Дата сессии
            </label>
            <input
              id="session-date-input"
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500 [color-scheme:dark]"
              data-testid="upload-modal-session-date"
            />
          </div>
          {/* Language pictogram — flag of the chosen transcription language.
              Click to open a dropdown of all 50+ languages Deepgram Nova-3
              supports (see lib/transcriptionLanguages.js). The choice rides
              along in the upload metadata and the backend stores it on the
              audio file row, so the later /transcribe call uses the right
              `language=` for Deepgram. Default is Russian. */}
          <div className="relative">
            <label className="block text-xs text-gray-400 mb-1.5">Язык</label>
            <button
              ref={langBtnRef}
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 bg-gray-900 border border-gray-700 hover:border-gray-600 rounded px-2.5 py-2 text-sm text-white transition-colors"
              title={`Язык транскрипции: ${selectedLang.name}${selectedLang.native ? ' — ' + selectedLang.native : ''}`}
              data-testid="upload-modal-language-button"
              aria-haspopup="listbox"
              aria-expanded={langOpen}
            >
              <span className="text-base leading-none" aria-hidden>{selectedLang.flag}</span>
              <ChevronDown size={14} className="text-gray-500" />
            </button>
            {langOpen && (
              <div
                ref={langPopRef}
                className="absolute right-0 top-[calc(100%+4px)] z-10 w-72 max-h-80 overflow-y-auto bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-1"
                role="listbox"
                data-testid="upload-modal-language-list"
              >
                {TRANSCRIPTION_LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                    role="option"
                    aria-selected={l.code === language}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors ${
                      l.code === language
                        ? 'bg-orange-500/20 text-orange-200'
                        : 'text-gray-200 hover:bg-gray-800'
                    }`}
                    data-testid={`upload-modal-language-option-${l.code}`}
                  >
                    <span className="text-base leading-none flex-shrink-0" aria-hidden>{l.flag}</span>
                    <span className="flex-1 truncate">
                      {l.name}
                      {l.native && (
                        <span className="text-gray-500"> — {l.native}</span>
                      )}
                    </span>
                    {l.hint && (
                      <span className="text-[10px] text-gray-500 flex-shrink-0">{l.hint}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mode toggle: single mp3 vs. dual synchronized tracks */}
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">Режим загрузки</label>
          <div className="grid grid-cols-2 gap-1 bg-gray-900 rounded p-0.5" role="radiogroup">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'single'}
              onClick={() => setMode('single')}
              className={`py-1.5 text-sm rounded transition-colors ${
                mode === 'single' ? 'bg-orange-500 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
              data-testid="upload-mode-single"
            >
              Один файл
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'dual'}
              onClick={() => setMode('dual')}
              className={`py-1.5 text-sm rounded transition-colors ${
                mode === 'dual' ? 'bg-orange-500 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
              data-testid="upload-mode-dual"
            >
              Два трека (эксперт + клиент)
            </button>
          </div>
          {mode === 'dual' && (
            <p className="text-[10px] text-gray-500 mt-1 leading-relaxed">
              Треки должны начинаться и заканчиваться одновременно. Спикеры будут определены
              по треку — без ошибок диаризации.
            </p>
          )}
        </div>

        {/* File picker(s) */}
        {mode === 'single' ? (
          <div>
            <label className="block text-xs text-gray-400 mb-1.5">MP3 файл</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,audio/mpeg"
              onChange={(e) => handleFileChosen(e.target.files?.[0])}
              className="hidden"
              data-testid="upload-modal-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded border transition-colors text-sm ${
                file
                  ? 'border-orange-500/60 bg-orange-500/10 text-white'
                  : 'border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600 hover:text-white'
              }`}
              data-testid="upload-modal-file-button"
            >
              <FileAudio size={16} className="flex-shrink-0" />
              <span className="truncate">{file ? file.name : 'Выбрать файл…'}</span>
              {file && (
                <span className="ml-auto text-xs text-gray-400">
                  {(file.size / 1024 / 1024).toFixed(1)} МБ
                </span>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {[
              { label: 'Дорожка эксперта (твоя)', ref: expertInputRef, file: expertFile, onChange: handleExpertChosen, tid: 'expert' },
              { label: 'Дорожка клиента', ref: clientInputRef, file: clientFile, onChange: handleClientChosen, tid: 'client' },
            ].map(({ label, ref, file: f, onChange, tid }) => (
              <div key={tid}>
                <label className="block text-xs text-gray-400 mb-1.5">{label}</label>
                <input
                  ref={ref}
                  type="file"
                  accept=".mp3,audio/mpeg"
                  onChange={(e) => onChange(e.target.files?.[0])}
                  className="hidden"
                  data-testid={`upload-modal-file-input-${tid}`}
                />
                <button
                  type="button"
                  onClick={() => ref.current?.click()}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded border transition-colors text-sm ${
                    f
                      ? 'border-orange-500/60 bg-orange-500/10 text-white'
                      : 'border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600 hover:text-white'
                  }`}
                  data-testid={`upload-modal-file-button-${tid}`}
                >
                  <FileAudio size={16} className="flex-shrink-0" />
                  <span className="truncate">{f ? f.name : 'Выбрать файл…'}</span>
                  {f && (
                    <span className="ml-auto text-xs text-gray-400">
                      {(f.size / 1024 / 1024).toFixed(1)} МБ
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Title preview */}
        <div className="bg-gray-900/70 rounded px-3 py-2 border border-gray-700">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Название в списке</div>
          <div className="text-sm text-orange-300 font-medium truncate" data-testid="upload-modal-title-preview">
            {title}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm transition-colors"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="flex-1 py-2 rounded bg-orange-500 hover:bg-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            data-testid="upload-modal-submit"
          >
            Загрузить
          </button>
        </div>
      </form>
    </div>
  );
};

export default UploadModal;
